import { passiveAuth, forceAuth } from '@xfinity/maw-fetch';
import Cookies from 'js-cookie';
import qs from 'qs';
import throttle from 'lodash.throttle';

import { getSession, disconnectSessions } from './session';
import apiClient from '../middleware/apiClient';
import { MACAROON, COTTON_MACAROON } from '../helpers/apis';

import {
  ALLOWED_AUTH_SESSIONS,
  forceLoginV1,
  passiveLoginV1,
  hasHintCookie,
  isAptEnabled,
} from '../helpers/auth';
import { callApiWithFallback } from '../helpers/apiFallback';
import cottonFlagManager from '../helpers/featureFlags/cotton';
import {
  getAccountIndex,
  setAccountIndex,
  setCspAccountToken,
  getAccountPerms,
  checkForLegacyAccountCookie,
} from '../helpers/account';
import { clearCache } from './account';
import { setGuid, kibanaLog } from '../helpers/logger';

// Fetch Macaroon
export const GET_AUTH = 'GET_AUTH';
export const GET_AUTH_SUCCESS = 'GET_AUTH_SUCCESS';
export const GET_AUTH_FAILURE = 'GET_AUTH_FAILURE';

export const SET_AUTH_STATUS = 'SET_AUTH_STATUS';
export const SET_FORCE_AUTHENTICATION = 'SET_FORCE_AUTHENTICATION';
export const SET_ACCOUNT_INDEX = 'SET_ACCOUNT_INDEX';
export const HAS_SELECTED_ACCOUNT = 'HAS_SELECTED_ACCOUNT';
export const SET_ACCOUNT_PERMS = 'SET_ACCOUNT_PERMS';

const cottonPassiveAuthTimeout = 15e3; // 15 seconds

function dispatchGetAuth(showLoading = true, isKotlinEnabled) {
  return {
    type: GET_AUTH,
    payload: apiClient.fetch(isKotlinEnabled ? COTTON_MACAROON : MACAROON),
    showLoading,
  };
}

function dispatchSetAccountPerms(payload) {
  return {
    type: SET_ACCOUNT_PERMS,
    payload,
  };
}

function dispatchGetAuthSuccess(payload) {
  return {
    type: GET_AUTH_SUCCESS,
    payload,
  };
}

function dispatchGetAuthFailure(error) {
  return {
    type: GET_AUTH_FAILURE,
    payload: error,
  };
}

function dispatchSetAuthStatus(payload) {
  return {
    type: SET_AUTH_STATUS,
    payload,
  };
}

function dispatchSetForceAuthentication() {
  return {
    type: SET_FORCE_AUTHENTICATION,
  };
}

function dispatchSetAccountIndex(payload) {
  return {
    type: SET_ACCOUNT_INDEX,
    payload,
  };
}

const setHasSelectedAccount = payload => ({
  type: HAS_SELECTED_ACCOUNT,
  payload,
});

export const setAuthStatus = status => dispatch => (
  dispatch(dispatchSetAuthStatus(status))
);

export const setAccountPerms = roles => async dispatch => dispatch(dispatchSetAccountPerms(roles));

const cottonPassiveAuth = throttle(
  () => {
    const { origin } = window.location;
    return new Promise((resolve, reject) => {
      let iframeStatus;

      const iframe = window.document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.onload = () => {
        iframeStatus = 'success';
        resolve();
      };
      iframe.onerror = () => {
        iframeStatus = 'fail';
        reject();
      };
      iframe.src = `${origin}/cotton/oauth/passive_connect?continue=${encodeURIComponent('https://customer.xfinity.com/assets/close.svg')}`;
      window.document.body.appendChild(iframe);

      // Remove iframe and reset data after requests have been reloaded
      setTimeout(() => {
        window.document.body.removeChild(iframe);
        if (!iframeStatus) {
          // status was never updated, iFrame is stuck, let's reject it
          reject(new Error('cotton passive auth timeout'));
        }
      }, cottonPassiveAuthTimeout);
    });
  },
  // throttle duration
  cottonPassiveAuthTimeout,
  // Execute the function immediately, do not execute at the end of the throttle period
  { leading: true, trailing: false },
);

const getValidSession = (dispatch, existingSession, isCottonSession) => {
  if (existingSession && ALLOWED_AUTH_SESSIONS.includes(existingSession.status)) {
    return existingSession;
  }
  return dispatch(getSession(isCottonSession));
};

// Helper wrapped around cotton passive auth with error handling
const getPassiveAuth = async () => {
  const {
    oauthEnabled,
    disableRubyApis,
    macaroonEnabled,
    isAptV1Enabled,
    cottonAllowed,
    masterFlag,
  } = cottonFlagManager;
  const shouldUseCottonPassiveAuth = oauthEnabled
    && (disableRubyApis || macaroonEnabled || isAptV1Enabled);

  if (oauthEnabled == null) {
    kibanaLog('cotton__oauth_flag_undefined');
  }

  // Cotton passive connect should only run when Cotton is enabled and traffic is allowed.
  if (masterFlag && cottonAllowed) {
    const sessionCookie = Cookies.get('SESSION');
    try {
      if (sessionCookie) {
        kibanaLog('session_is_present_before_cotton_passive_connect');
      } else {
        kibanaLog('session_is_not_present_before_cotton_passive_connect');
      }
      await cottonPassiveAuth();
      kibanaLog('cotton_passive_connect_success');
    } catch (error) {
      kibanaLog('cotton_passive_connect_error', {
        message: error.message,
      });
    }
  }

  if (shouldUseCottonPassiveAuth && Cookies.get('isAuth') !== '1') {
    // If the isAuth cookie isn't set correctly, then something is wrong.
    // Force auth in this case.
    disconnectSessions();

    const authError = new Error('Invalid auth cookie');
    authError.forceAuthentication = true;
    throw authError;
  }

  if (!disableRubyApis) {
    // Ruby passive connect after Cotton passive connect
    try {
      await passiveAuth();
    } catch (error) {
      kibanaLog('ruby_passive_connect_error', {
        message: error.message,
      });
    }
  }
};

const getMacaroon = (
  isCottonSession,
  showLoading = true,
  existingSession,
) => async (dispatch, getState) => {
  checkForLegacyAccountCookie();

  let response;
  if (cottonFlagManager.macaroonEnabled || cottonFlagManager.disableRubyApis) {
    // Try Cotton API, fallback to Ruby API (fallback is skipped when disableRubyApis is true)
    response = await callApiWithFallback(
      dispatch,
      () => dispatchGetAuth(showLoading, true), // Cotton API call
      () => dispatchGetAuth(showLoading, false), // Ruby API call
      'macaroon',
    );
  } else {
    // Only call Ruby API
    response = await dispatch(dispatchGetAuth(showLoading, false)).payload;
  }
  // Re-fetch session only when no existing allowed session was provided/available.
  // If an existing session already has an allowed status, reuse it to
  // avoid a duplicate session API call.
  const session = await getValidSession(dispatch, existingSession, isCottonSession);

  if (ALLOWED_AUTH_SESSIONS.indexOf(session.status) === -1) {
    throw new Error(`invalid session status: ${session.status}`);
  }

  dispatch(setAuthStatus(session.status));

  // On initial page load don't validate the account index, just set it.
  // The first time getAuth is called auth.accountIndex should be undefined.
  // On reauth, auth.accountIndex should be set, so we should
  // validate it didn't change in setAccountIndex.
  const { auth: { accountIndex: currentAccountIndex, hasSelectedAccount } = {} } = getState();
  const validateAccountIndex = Number.isInteger(currentAccountIndex);

  // Set the CSP Auth header and cookie for the selected account once macaroon returns.
  const accountIndex = getAccountIndex(response);
  setAccountIndex(accountIndex, validateAccountIndex);
  dispatch(dispatchSetAccountIndex(accountIndex));
  setCspAccountToken(response, accountIndex);

  setGuid(response.guid);
  // Only log Cotton-specific success event when Cotton API was actually used
  if (cottonFlagManager.macaroonEnabled || cottonFlagManager.disableRubyApis) {
    kibanaLog('cotton_macaroon_api_success');
  }
  if (!hasSelectedAccount) {
    // Read the selected account query param to determine if we've selected an account yet.
    // This is intended for smoothly handling page reloads after the account selector flow.
    // The "sa" query param is short for "selected account"
    const { sa: selectedAccountParam } = qs.parse(window.location.search.slice(1));
    dispatch(setHasSelectedAccount(selectedAccountParam === 'true'));
  }

  // set account perms
  await dispatch(setAccountPerms(getAccountPerms(response, accountIndex)));

  return dispatch(dispatchGetAuthSuccess(response));
};

export const getAuth = (showLoading = true) => async (dispatch, getState) => {
  const isAptV1Enabled = isAptEnabled();
  // If APT v1 is disabled, fall back to the old getAuthExisting
  if (!isAptV1Enabled) {
    return getAuthExisting(showLoading)(dispatch, getState);
  }
  try {
    const { oauthEnabled, disableRubyApis } = cottonFlagManager;
    const isCottonSession = disableRubyApis || oauthEnabled;
    // --- Check r-hint cookie ---
    const hasHint = hasHintCookie();
    kibanaLog('apt_oauth_v1_routing_decision', {
      apt_enabled: String(isAptV1Enabled),
      oauth_enabled: String(oauthEnabled),
      disable_ruby_apis: String(disableRubyApis),
      is_cotton_session: String(isCottonSession),
      has_hint_cookie: String(hasHint),
      force_auth_base_path: '/cotton',
      passive_auth_base_path: '/cotton',
    });
    if (!hasHint) {
    // If r-hint missing → force login
      kibanaLog('apt_oauth_v1_force_redirect', {
        force_auth_base_path: '/cotton',
      });
      forceLoginV1({ basePath: '/cotton' });
      return null; // Prevent further execution after redirect; flow resumes after re-authentication
    }

    const session = await getValidSession(dispatch, undefined, isCottonSession);
    const sessionStatus = (session && session.status) ? session.status : 'undefined';
    kibanaLog('apt_oauth_v1_session_status', {
      session_status: sessionStatus,
      is_allowed_session: String(ALLOWED_AUTH_SESSIONS.includes(sessionStatus)),
    });
    // If session is invalid → passive login
    if (!ALLOWED_AUTH_SESSIONS.includes(sessionStatus)) {
      kibanaLog('apt_oauth_v1_passive_redirect', {
        is_cotton_session: String(isCottonSession),
        session_status: sessionStatus,
        passive_auth_base_path: '/cotton',
      });
      passiveLoginV1({ basePath: '/cotton' });
      return null; // Prevent further execution after redirect; flow resumes after re-authentication
    }

    // Only call getMacaroon (which triggers account API) if user is authenticated
    if (session && session.status === 'LOGIN FULL' && hasHint) {
      // Temporary fix to check for the legacy account switcher cookie BEFORE calling macaroon
      // to create a smooth rollout until account selection
      // changes are deployed for all frontend apps
      // TODO: Remove this call when maw_cspTokenIndex is cleaned up.
      checkForLegacyAccountCookie();
      return getMacaroon(isCottonSession, showLoading, session)(dispatch, getState);
    }
    // If not authenticated, do not call getMacaroon/account API
    return null;
  } catch (error) {
    if (error.forceAuthentication) {
      dispatch(dispatchSetForceAuthentication());
      kibanaLog('apt_oauth_v1_force_redirect', {
        force_auth_base_path: '/cotton',
      });
      forceLoginV1({ basePath: '/cotton' });
      return null;
    }
    dispatch(dispatchGetAuthFailure(error));
    throw error;
  }
};

export const getAuthExisting = (showLoading = true) => async (dispatch, getState) => {
  try {
    const { oauthEnabled, disableRubyApis } = cottonFlagManager;
    const isCottonSession = disableRubyApis || oauthEnabled;
    // check for r-hint cookie
    // If r-hint exists, then we can continue with passiveAuth and get session
    // If r-hint does not exist, call forceLogin for Cotton sessions only
    if (!Cookies.get('r-hint') && isCottonSession) {
      await forceAuth();
    }

    let session = await getValidSession(dispatch, undefined, isCottonSession);
    await getPassiveAuth();

    // Temporary fix to check for the legacy account switcher cookie BEFORE calling macaroon
    // to create a smooth rollout until account selection changes are deployed for all frontend apps
    // TODO: Remove this call when maw_cspTokenIndex is cleaned up.
    checkForLegacyAccountCookie();

    let response;
    if (cottonFlagManager.macaroonEnabled || cottonFlagManager.disableRubyApis) {
      // Try Cotton API, fallback to Ruby API (fallback is skipped when disableRubyApis is true)
      response = await callApiWithFallback(
        dispatch,
        () => dispatchGetAuth(showLoading, true), // Cotton API call
        () => dispatchGetAuth(showLoading, false), // Ruby API call
        'macaroon',
      );
    } else {
      // Only call Ruby API
      response = await dispatch(dispatchGetAuth(showLoading, false)).payload;
    }
    // Only re-fetch session if passive auth may have changed it (i.e. first session was not valid).
    // If it was already LOGIN FULL, reuse it to avoid a duplicate session API call.
    // Re-validate session after passive auth using the active session mode.
    session = await getValidSession(dispatch, session, isCottonSession);

    if (ALLOWED_AUTH_SESSIONS.indexOf(session.status) === -1) {
      throw new Error(`invalid session status: ${session.status}`);
    }

    dispatch(setAuthStatus(session.status));

    // On initial page load don't validate the account index, just set it.
    // The first time getAuth is called auth.accountIndex should be undefined.
    // On reauth, auth.accountIndex should be set, so we should
    // validate it didn't change in setAccountIndex.
    const { auth: { accountIndex: currentAccountIndex, hasSelectedAccount } = {} } = getState();
    const validateAccountIndex = Number.isInteger(currentAccountIndex);

    // Set the CSP Auth header and cookie for the selected account once macaroon returns.
    const accountIndex = getAccountIndex(response);
    setAccountIndex(accountIndex, validateAccountIndex);
    dispatch(dispatchSetAccountIndex(accountIndex));
    setCspAccountToken(response, accountIndex);

    setGuid(response.guid);

    if (!hasSelectedAccount) {
      // Read the selected account query param to determine if we've selected an account yet.
      // This is intended for smoothly handling page reloads after the account selector flow.
      // The "sa" query param is short for "selected account"
      const { sa: selectedAccountParam } = qs.parse(window.location.search.slice(1));
      dispatch(setHasSelectedAccount(selectedAccountParam === 'true'));
    }

    // set account perms
    await dispatch(setAccountPerms(getAccountPerms(response, accountIndex)));

    return dispatch(dispatchGetAuthSuccess(response));
  } catch (error) {
    if (error.forceAuthentication) {
      return dispatch(dispatchSetForceAuthentication());
    }
    dispatch(dispatchGetAuthFailure(error));
    throw error;
  }
};

// Close dispatch in the event handler for use whenever it's needed later.
export const addRefreshAuthListener = () => (dispatch) => {
  window.addEventListener('refreshAuth', async ({ authPromise }) => {
    try {
      await dispatch(getAuth(false));
      authPromise.resolve();
    } catch (error) {
      authPromise.reject(error);
      throw error;
    }
  });
};

export const switchAccount = index => async (dispatch, getState) => {
  const { auth: { accountIndex, macaroon } } = getState();
  // Save the index to the account selection cookie
  setAccountIndex(index, false);
  // Mark that the user has selected an account.
  dispatch(setHasSelectedAccount(true));

  if (index !== accountIndex) {
    // Update the CSP-Auth header for the newly selected account before calling getAuth so the
    // macaroon fetch below reflects the new account instead of the previous one. Otherwise the
    // response still reports the old account, which mismatches the cookie set above and
    // triggers an extra, unintended reload from setAccountIndex's mismatch guard.
    setCspAccountToken(macaroon || {}, index);
    // If the account index has changed, call getAuth to:
    // - Get a new user-with-account access_token from CIMA
    // - Update the CSP-Auth header for the selected account
    // - Update auth.accountIndex in the store
    await dispatch(getAuth(false));
    // Bust cached data in the store so that the next render will fetch fresh data
    // for the newly selected account.
    dispatch(clearCache());
  }
};
